import {
	ArrowLeftOutlined,
	ClockCircleOutlined,
	EnvironmentOutlined,
	FieldTimeOutlined,
} from "@ant-design/icons";
import { Button, Card, DatePicker, Empty, Spin, Statistic, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import L from "leaflet";
import { useEffect, useMemo } from "react";
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useStaff } from "@/modules/staff/hooks/useStaff";
import { useUrlParam } from "@/shared/hooks/useUrlState";
import { useStaffLocationHistory } from "../hooks/useStaffLocation";
import type { StaffLocationHistoryItem } from "../types";

const { Title, Text } = Typography;

function createPinIcon(color: string) {
	return L.divIcon({
		className: "",
		html: `
			<svg xmlns="http://www.w3.org/2000/svg" width="28" height="38" viewBox="0 0 28 38">
				<path d="M14 0C6.268 0 0 6.268 0 14c0 9.333 14 24 14 24S28 23.333 28 14C28 6.268 21.732 0 14 0z" fill="${color}" stroke="white" stroke-width="2"/>
				<circle cx="14" cy="14" r="5" fill="white"/>
			</svg>`,
		iconSize: [28, 38],
		iconAnchor: [14, 38],
		popupAnchor: [0, -38],
	});
}

const startIcon = createPinIcon("#22c55e");
const endIcon = createPinIcon("#ef4444");

const DEFAULT_CENTER: [number, number] = [41.56, 60.62];
const DEFAULT_ZOOM = 13;

function formatDateTime(value: string | undefined): string {
	return value ? new Date(value).toLocaleString("uz-UZ") : "—";
}

function RouteBoundsFitter({ points }: { points: StaffLocationHistoryItem[] }) {
	const map = useMap();

	useEffect(() => {
		if (points.length === 0) {
			return;
		}
		const latLngs = points.map((p) => [p.latitude, p.longitude] as [number, number]);
		map.fitBounds(L.latLngBounds(latLngs), { padding: [30, 30], maxZoom: 16 });
	}, [map, points]);

	return null;
}

interface RouteMapProps {
	items: StaffLocationHistoryItem[];
	isFetching: boolean;
}

function RouteMap({ items, isFetching }: RouteMapProps) {
	const positions = useMemo(
		() => items.map((p) => [p.latitude, p.longitude] as [number, number]),
		[items]
	);
	const firstPoint = items[0];
	const lastPoint = items[items.length - 1];

	return (
		<div className="relative h-[calc(100vh-360px)] min-h-[420px] w-full overflow-hidden rounded-lg border border-neutral-200">
			{isFetching && (
				<div className="absolute right-3 top-3 z-[1000] rounded-md bg-white/90 px-2 py-1 shadow">
					<Spin size="small" />
				</div>
			)}
			<MapContainer
				center={firstPoint ? [firstPoint.latitude, firstPoint.longitude] : DEFAULT_CENTER}
				zoom={DEFAULT_ZOOM}
				className="h-full w-full"
				scrollWheelZoom
			>
				<TileLayer
					attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
					url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
				/>
				<RouteBoundsFitter points={items} />

				{/* Route polyline */}
				{positions.length > 1 && (
					<Polyline positions={positions} pathOptions={{ color: "#1677ff", weight: 4 }} />
				)}

				{/* Start marker */}
				{firstPoint && (
					<Marker position={[firstPoint.latitude, firstPoint.longitude]} icon={startIcon}>
						<Popup>
							<div className="text-sm min-w-[140px]">
								<strong style={{ color: "#22c55e" }}>Boshlanish</strong>
								<br />
								<span className="text-gray-500">{formatDateTime(firstPoint.recordedAt)}</span>
							</div>
						</Popup>
					</Marker>
				)}

				{/* End marker */}
				{lastPoint && lastPoint.id !== firstPoint?.id && (
					<Marker position={[lastPoint.latitude, lastPoint.longitude]} icon={endIcon}>
						<Popup>
							<div className="text-sm min-w-[140px]">
								<strong style={{ color: "#ef4444" }}>Tugash</strong>
								<br />
								<span className="text-gray-500">{formatDateTime(lastPoint.recordedAt)}</span>
							</div>
						</Popup>
					</Marker>
				)}
			</MapContainer>
		</div>
	);
}

export function StaffRouteHistoryPage() {
	const { staffId } = useParams<{ staffId: string }>();
	const navigate = useNavigate();
	// Date lives in the URL (?date=YYYY-MM-DD); absent means today.
	const [dateParam, setDateParam] = useUrlParam("date");
	const parsedDate = dateParam ? dayjs(dateParam) : null;
	const date: Dayjs = parsedDate?.isValid() ? parsedDate : dayjs();

	const range = useMemo(
		() => ({
			from: date.startOf("day").toISOString(),
			to: date.endOf("day").toISOString(),
		}),
		[date]
	);

	const { data: staff } = useStaff(staffId ?? "");
	const { data: points, isLoading, isFetching } = useStaffLocationHistory(staffId ?? "", range);

	const items = useMemo(() => points ?? [], [points]);
	const firstPoint = items[0];
	const lastPoint = items[items.length - 1];

	if (!staffId) {
		return <Navigate to="/monitoring" replace />;
	}

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex items-center gap-3">
					<Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
						Orqaga
					</Button>
					<Title level={4} className="!mb-0">
						{staff?.fullname ? `${staff.fullname} — marshrut` : "Xodim marshruti"}
					</Title>
				</div>
				<DatePicker
					value={date}
					onChange={(value) =>
						value && setDateParam(value.isSame(dayjs(), "day") ? null : value.format("YYYY-MM-DD"))
					}
					allowClear={false}
					disabledDate={(d) => d.isAfter(dayjs(), "day")}
					placeholder="Sanani tanlang"
				/>
			</div>

			<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
				<Card size="small">
					<Statistic
						title="Nuqtalar soni"
						value={items.length}
						prefix={<EnvironmentOutlined />}
						loading={isLoading}
					/>
				</Card>
				<Card size="small">
					<Statistic
						title="Birinchi nuqta"
						value={firstPoint ? formatDateTime(firstPoint.recordedAt) : "—"}
						prefix={<ClockCircleOutlined />}
						loading={isLoading}
					/>
				</Card>
				<Card size="small">
					<Statistic
						title="Oxirgi nuqta"
						value={lastPoint ? formatDateTime(lastPoint.recordedAt) : "—"}
						prefix={<FieldTimeOutlined />}
						loading={isLoading}
					/>
				</Card>
			</div>

			<Card size="small">
				{isLoading ? (
					<div className="flex h-[420px] items-center justify-center">
						<Spin />
					</div>
				) : items.length === 0 ? (
					<div className="flex h-[420px] flex-col items-center justify-center">
						<Empty description="Tanlangan kunda joylashuv ma'lumotlari topilmadi" />
					</div>
				) : (
					<RouteMap items={items} isFetching={isFetching} />
				)}
				{items.length > 0 && (
					<Text type="secondary" className="mt-2 block text-xs">
						Marshrut planshetdan kelgan joylashuv nuqtalari asosida chizilgan.
					</Text>
				)}
			</Card>
		</div>
	);
}
